import { useCallback, useState } from 'react';

import { gql } from '@apollo/client';

import {
  OdProtosPartnershipData_PartnershipType as PartnershipType,
  OdProtosPartnershipData_Referral_AttributionType as ReferralAttributionType,
} from '__generated__/globalTypes';

import { useQuery } from 'platform/apollo';

import { offerPollingTimeoutThreshold } from 'shared/helpers/globals';
import { useTimeout } from 'shared/helpers/hooks/useTimeout';

import {
  GetReferralInvitationEligibility,
  GetReferralInvitationEligibilityVariables,
} from 'growth/__generated__/GetReferralInvitationEligibility';
import {
  PartnershipReferrals,
  PartnershipReferralsVariables,
  PartnershipReferrals_partnership_getReferralsForSellerInput_referrals as Referral,
  PartnershipReferrals_partnership_getReferralInvitation_invitation as ReferralInvitation,
} from 'growth/__generated__/PartnershipReferrals';

export interface PartnershipReferralResponse {
  refetch: () => void;
  loading: boolean;
  isPartnershipLead: boolean;
  hasHomebuilderReferral: boolean;
  hasOreReferral: boolean;
  hasAgentReferral: boolean;
  agentReferral?: Referral;
  partnershipIdentifiers?: Array<string>;
  partnershipAttributes?: {
    partnership_types: string;
    partnership_identifiers: string;
  };
  referralInvitiationEligibility: boolean;
  referralInvitiationPaymentEligibility: boolean;
  referralInvitation: ReferralInvitation | null;
  isTrueAgentReferral: boolean;
}

export type PartnershipAttributes = {
  partnership_types: string;
  partnership_identifiers: string;
};

export const PARTNERSHIP_REFERRAL_QUERY = gql`
  query PartnershipReferrals($sellerInputUUID: String!) {
    partnership {
      getReferralsForSellerInput(input: { sellerInputId: $sellerInputUUID }) {
        referrals {
          sellerRelation
          partnershipType
          partnerIdentifier
          attributionType
          uuid
          attributes {
            eligibleForAgentReferralPayment
          }
        }
      }
      getReferralInvitation(input: { sellerInputId: $sellerInputUUID }) {
        invitation {
          id
          status
          inviterName
          inviterEmail
        }
      }
    }
  }
`;

export const GET_REFERRAL_INVITATION_ELIGIBILITY = gql`
  query GetReferralInvitationEligibility($sellerInputUUID: String!) {
    partnership {
      getReferralInvitationEligibility(input: { sellerInputId: $sellerInputUUID }) {
        eligible
        paymentEligible
        eligibilityReady
      }
    }
  }
`;

const ORPHANED_PARTNERSHIP_TYPES = [
  PartnershipType.CORPORATE_BENEFITS,
  PartnershipType.INDEPENDENT_DEVELOPER,
  PartnershipType.REIT,
  PartnershipType.MARKETING,
];

// ORE, LENDER, CREDIT_AND_DEBT, AND TEST_PARTNER all get the "ORE" Experience
const ORE_PARTNERSHIP_TYPES = [
  PartnershipType.ONLINE_REAL_ESTATE,
  PartnershipType.LENDER,
  PartnershipType.CREDIT_AND_DEBT,
  PartnershipType.TEST_PARTNER,
];

// REAL_ESTATE_AGENCY get the "Agent" Experience
const AGENT_PARTNERSHIP_TYPES = [PartnershipType.REAL_ESTATE_AGENCY];

/** Fetch partnership referral data */
export const usePartnershipReferrals = (sellerInputUUID?: string): PartnershipReferralResponse => {
  const skip = sellerInputUUID?.trim().length === 0;
  const referralQuery = useQuery<PartnershipReferrals, PartnershipReferralsVariables>(
    PARTNERSHIP_REFERRAL_QUERY,
    {
      variables: { sellerInputUUID: sellerInputUUID as string },
      skip,
      fetchPolicy: 'cache-first',
      context: { linkContext: { team: 'growth' } },
    },
  );

  const referralInvitationQuery = useQuery<
    GetReferralInvitationEligibility,
    GetReferralInvitationEligibilityVariables
  >(GET_REFERRAL_INVITATION_ELIGIBILITY, {
    variables: { sellerInputUUID: sellerInputUUID as string },
    skip,
    pollInterval: 800,
    context: { linkContext: { team: 'growth' } },
  });

  const [timeoutElapsed, setTimeoutElapsed] = useState(false);
  useTimeout(() => setTimeoutElapsed(true), offerPollingTimeoutThreshold);

  const shouldStopPolling =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.eligibilityReady ||
    timeoutElapsed;

  if (shouldStopPolling) {
    referralInvitationQuery.stopPolling();
  }

  const refetch = useCallback(() => {
    // Refetches don't honor useQuery's `skip` config,
    // see https://github.com/apollographql/apollo-client/issues/8270
    if (!skip) {
      referralQuery.refetch();
      referralInvitationQuery.refetch();
    }
  }, [skip, referralQuery, referralInvitationQuery]);

  const loading = referralQuery.loading || referralInvitationQuery.loading;

  const referralInvitiationEligibility =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.eligible || false;
  const referralInvitation =
    referralQuery.data?.partnership.getReferralInvitation?.invitation || null;
  const referralInvitiationPaymentEligibility =
    referralInvitationQuery.data?.partnership.getReferralInvitationEligibility?.paymentEligible ||
    false;

  // Filter ORPHANED_PARTNERSHIP_TYPES out from response and set referral attributes based on response data
  const allReferrals = referralQuery.data?.partnership.getReferralsForSellerInput?.referrals || [];

  let hasHomebuilderReferral = false;
  let hasOreReferral = false;
  let hasAgentReferral = false;
  let isTrueAgentReferral = false;
  let agentReferral = undefined;
  const partnershipReferrals: Referral[] = [];
  const partnershipIdentifiers: string[] = [];
  const partnershipTypes: PartnershipType[] = [];
  for (const referral of allReferrals) {
    if (ORPHANED_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      continue;
    }
    if (referral.partnershipType === PartnershipType.HOME_BUILDER) {
      hasHomebuilderReferral = true;
    }
    if (ORE_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      hasOreReferral = true;
    }
    if (AGENT_PARTNERSHIP_TYPES.includes(referral.partnershipType)) {
      hasAgentReferral = true;
      agentReferral = referral;
    }
    partnershipReferrals.push(referral);
    partnershipIdentifiers.push(referral.partnerIdentifier);
    partnershipTypes.push(referral.partnershipType);
  }

  if (agentReferral?.attributionType === ReferralAttributionType.REFERRAL) {
    isTrueAgentReferral = true;
  }
  const partnershipTypesString = partnershipTypes
    .map((type) => type.toString().toLowerCase())
    .join();
  const partnershipIdentifiersString = partnershipIdentifiers.map((id) => id.toLowerCase()).join();

  // Concat partnershipTypesString and partnershipIdentifiersString with "," in the head and tail
  // Since in optimizely, it matches by checking if a string is contained or not,
  // therefore using ",lennar," will help differentiate case: "lennar", and "lennar_type"
  const partnershipAttributes: PartnershipAttributes = {
    partnership_types:
      partnershipTypesString.length > 0
        ? ',' + partnershipTypesString + ','
        : partnershipTypesString,
    partnership_identifiers:
      partnershipIdentifiersString.length > 0
        ? ',' + partnershipIdentifiersString + ','
        : partnershipIdentifiersString,
  };

  return {
    ...referralQuery,
    ...referralInvitationQuery,
    refetch,
    loading,
    isPartnershipLead: !!partnershipReferrals?.length,
    hasHomebuilderReferral,
    hasOreReferral,
    hasAgentReferral,
    agentReferral,
    partnershipIdentifiers,
    partnershipAttributes,
    referralInvitiationEligibility,
    referralInvitiationPaymentEligibility,
    referralInvitation,
    isTrueAgentReferral,
  };
};
